import * as React from 'react';

import type { ToastActionElement, ToastProps } from '@/components/ui/toast';

const TOAST_LIMIT = 5;
const DEFAULT_DURATION = 5000; // 5 seconds default auto-dismiss

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  duration?: number; // auto-dismiss duration in ms
};

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type Action =
  | {
      type: 'ADD_TOAST';
      toast: ToasterToast;
    }
  | {
      type: 'UPDATE_TOAST';
      toast: Partial<ToasterToast>;
    }
  | {
      type: 'DISMISS_TOAST';
      toastId?: ToasterToast['id'];
    }
  | {
      type: 'REMOVE_TOAST';
      toastId?: ToasterToast['id'];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const autoDismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string, delay: number = 1000) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    });
  }, delay);

  toastTimeouts.set(toastId, timeout);
};

const scheduleAutoDismiss = (toastId: string, duration: number) => {
  // Clear existing auto-dismiss timer if any
  if (autoDismissTimeouts.has(toastId)) {
    clearTimeout(autoDismissTimeouts.get(toastId));
  }

  const timeout = setTimeout(() => {
    autoDismissTimeouts.delete(toastId);
    dispatch({
      type: 'DISMISS_TOAST',
      toastId: toastId,
    });
  }, duration);

  autoDismissTimeouts.set(toastId, timeout);
};

const cancelAutoDismiss = (toastId: string) => {
  if (autoDismissTimeouts.has(toastId)) {
    clearTimeout(autoDismissTimeouts.get(toastId));
    autoDismissTimeouts.delete(toastId);
  }
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Toast = Omit<ToasterToast, 'id'>;

type ToastVariant = 'default' | 'destructive' | 'success' | 'warning' | 'info';

interface ToastOptions {
  title?: string;
  description?: string;
  action?: ToastActionElement;
  variant?: ToastVariant;
  duration?: number; // ms, set to 0 for no auto-dismiss
}

function toast({ duration = DEFAULT_DURATION, ...props }: Toast) {
  const id = genId();

  const update = (updateProps: ToasterToast) => {
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...updateProps, id },
    });
  };
  const dismiss = () => {
    cancelAutoDismiss(id);
    dispatch({ type: 'DISMISS_TOAST', toastId: id });
  };

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      duration,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  // Schedule auto-dismiss if duration is positive
  if (duration > 0) {
    scheduleAutoDismiss(id, duration);
  }

  return {
    id: id,
    dismiss,
    update,
  };
}

/**
 * Convenience function for success toasts.
 */
toast.success = (options: Omit<ToastOptions, 'variant'>) => {
  return toast({ ...options, variant: 'success' });
};

/**
 * Convenience function for error toasts.
 */
toast.error = (options: Omit<ToastOptions, 'variant'>) => {
  return toast({ ...options, variant: 'destructive' });
};

/**
 * Convenience function for info toasts.
 */
toast.info = (options: Omit<ToastOptions, 'variant'>) => {
  return toast({ ...options, variant: 'info' });
};

/**
 * Convenience function for warning toasts.
 */
toast.warning = (options: Omit<ToastOptions, 'variant'>) => {
  return toast({ ...options, variant: 'warning' });
};

/**
 * Hook for managing toast notifications.
 *
 * Provides access to current toasts and a toast() function
 * to create new notifications.
 *
 * @example
 * const { toast } = useToast();
 *
 * toast({
 *   title: "Success!",
 *   description: "Your changes have been saved.",
 * });
 *
 * toast({
 *   variant: "destructive",
 *   title: "Error",
 *   description: "Something went wrong.",
 * });
 */
function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => {
      if (toastId !== undefined) {
        dispatch({ type: 'DISMISS_TOAST', toastId });
      } else {
        dispatch({ type: 'DISMISS_TOAST' });
      }
    },
  };
}

/**
 * Reset toast state. Used for testing to ensure clean state between tests.
 * @internal
 */
function resetToastState() {
  // Clear all timeouts
  toastTimeouts.forEach((timeout) => {
    clearTimeout(timeout);
  });
  toastTimeouts.clear();
  autoDismissTimeouts.forEach((timeout) => {
    clearTimeout(timeout);
  });
  autoDismissTimeouts.clear();

  // Reset state
  memoryState = { toasts: [] };

  // Notify listeners of state change
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

export { useToast, toast, resetToastState };
export type { ToastOptions, ToastVariant };
